const viteEnv = typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
  ? import.meta.env
  : undefined;

const GA_MEASUREMENT_ID = viteEnv?.VITE_GA_MEASUREMENT_ID?.trim();
const GA_DEBUG_MODE = viteEnv?.VITE_GA_DEBUG_MODE === "true";
export const CONSENT_STORAGE_KEY = "cookieConsent";
export const CONSENT_CHANGE_EVENT = "rugira:cookie-consent-change";
const GTAG_SCRIPT_ID = "ga4-gtag";

type ConsentOptions = { analytics: boolean };
type TrackParams = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    google_tag_manager?: Record<string, unknown>;
  }
}

const isBrowser = typeof window !== "undefined";
let initialized = false;
let gtagScriptPromise: Promise<void> | null = null;
let configured = false;

const ensureDataLayer = () => {
  if (!isBrowser) return;
  window.dataLayer = window.dataLayer || [];
};

const ensureGtagStub = () => {
  if (!isBrowser) return;
  ensureDataLayer();
  if (!window.gtag) {
    window.gtag = (...args: any[]) => {
      window.dataLayer.push(args);
    };
  }
};

const getConsentStored = () => {
  if (!isBrowser) return false;
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const getStoredAnalyticsConsent = () => getConsentStored();

export const getStoredConsentValue = () => {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
};

const sanitizeLabel = (value?: string) => {
  if (!value) return undefined;
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return undefined;
  if (normalized.includes("@")) return "Email";
  return normalized.slice(0, 120);
};

const getHrefMeta = (href?: string) => {
  if (!href || !isBrowser) {
    return { hrefDomain: undefined, isOutbound: false };
  }

  if (href.startsWith("mailto:")) return { hrefDomain: "mailto", isOutbound: true };
  if (href.startsWith("tel:")) return { hrefDomain: "tel", isOutbound: true };

  try {
    const url = new URL(href, window.location.href);
    return { hrefDomain: url.hostname, isOutbound: url.hostname !== window.location.hostname };
  } catch {
    return { hrefDomain: undefined, isOutbound: false };
  }
};

const getBaseParams = () => {
  if (!isBrowser) return {};
  return {
    page_path: window.location.pathname || "/",
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_title: document.title,
    language: document.documentElement.lang || undefined,
    ...(GA_DEBUG_MODE || viteEnv?.DEV ? { debug_mode: true } : {}),
  };
};

const loadGtagScript = () => {
  if (!isBrowser || !GA_MEASUREMENT_ID) return Promise.resolve();
  if (gtagScriptPromise) return gtagScriptPromise;
  if (document.getElementById(GTAG_SCRIPT_ID)) return Promise.resolve();

  gtagScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = GTAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load gtag.js"));
    document.head.appendChild(script);
  });

  return gtagScriptPromise;
};

const configureGa4 = async () => {
  if (!isBrowser || !GA_MEASUREMENT_ID || configured) return;
  await loadGtagScript();
  ensureGtagStub();
  window.gtag?.("js", new Date());
  window.gtag?.("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });
  configured = true;
};

export const initAnalytics = () => {
  if (!isBrowser || initialized) return;
  ensureGtagStub();
  window.gtag?.("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  initialized = true;
};

export const setConsent = async ({ analytics }: ConsentOptions) => {
  if (!isBrowser) return;
  initAnalytics();
  ensureGtagStub();

  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (analytics) {
    await configureGa4();
  }
};

export const track = (eventName: string, params: TrackParams = {}) => {
  if (!isBrowser) return;
  initAnalytics();
  if (!getConsentStored()) return;
  if (!GA_MEASUREMENT_ID) {
    if (viteEnv?.DEV) {
      console.debug(`[ga4 disabled] ${eventName}`, params);
    }
    return;
  }

  const enrichedParams = {
    ...getBaseParams(),
    ...params,
  };

  if (window.gtag) {
    window.gtag("event", eventName, enrichedParams);
    return;
  }

  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...enrichedParams });
    return;
  }

  if (viteEnv?.DEV) {
    console.debug(`[ga4] ${eventName}`, params);
  }
};

export const trackSectionView = (sectionId: string) => {
  if (!sectionId || !isBrowser) return;
  track("section_view", {
    section_id: sectionId,
    page_path: window.location.pathname || "/",
  });
};

export const trackScrollDepth = (percent: number) => {
  if (!percent || !isBrowser) return;
  track("scroll_depth", {
    percent,
    page_path: window.location.pathname || "/",
  });
};

export const trackModalOpen = (payload: {
  modal_id: string;
  trigger?: "cta" | "hash" | "event";
  section_id?: string;
}) => {
  if (!payload.modal_id) return;
  track("modal_open", payload);
};

export const trackModalClose = (payload: {
  modal_id: string;
  method?: "button" | "escape" | "overlay";
}) => {
  if (!payload.modal_id) return;
  track("modal_close", payload);
};

export const trackCtaClick = (payload: {
  cta_id: string;
  cta_label?: string;
  section_id?: string;
  href?: string;
  is_outbound?: boolean;
}) => {
  if (!payload.cta_id) return;
  const { hrefDomain, isOutbound } = getHrefMeta(payload.href);
  track("cta_click", {
    cta_id: payload.cta_id,
    cta_label: sanitizeLabel(payload.cta_label),
    section_id: payload.section_id,
    href_domain: hrefDomain,
    is_outbound: payload.is_outbound ?? isOutbound,
  });
};

export const trackFormStart = (payload: { form_id: string; section_id?: string }) => {
  if (!payload.form_id) return;
  track("form_start", payload);
};

export const trackFormSubmit = (payload: {
  form_id: string;
  section_id?: string;
  outcome: "success" | "error";
}) => {
  if (!payload.form_id) return;
  track("form_submit", payload);
};

export const trackGenerateLead = (payload: {
  lead_type: string;
  form_id: string;
  section_id?: string;
}) => {
  if (!payload.form_id || !payload.lead_type) return;
  track("generate_lead", payload);
};

export const trackEarlyAccessRequested = (payload: {
  organizationType: string;
  role: string;
  countryJurisdiction: string;
  currentTradingSetup: string;
  primaryInterest: string;
  timeline: string;
  hasProfessionalEmail: boolean;
  language: string;
}) => {
  track("early_access_requested", {
    organization_type: payload.organizationType,
    role: payload.role,
    country_jurisdiction: payload.countryJurisdiction,
    current_trading_setup: payload.currentTradingSetup,
    primary_interest: payload.primaryInterest,
    timeline: payload.timeline,
    has_professional_email: payload.hasProfessionalEmail,
    language: payload.language,
  });
};

export const trackPricingPlanSelect = (payload: {
  plan_name: string;
  currency: "CHF";
  section_id: "pricing";
}) => {
  if (!payload.plan_name) return;
  track("pricing_plan_select", {
    ...payload,
    selected_currency: payload.currency,
  });
};

export const trackFaqToggle = (payload: { faq_id: string; is_open: boolean }) => {
  if (!payload.faq_id) return;
  track("faq_toggle", {
    ...payload,
    section_id: "faq",
  });
};

export const trackOutboundClick = (payload: {
  link_domain: string;
  link_label?: string;
  section_id?: string;
}) => {
  if (!payload.link_domain) return;
  track("outbound_click", {
    link_domain: payload.link_domain,
    link_label: sanitizeLabel(payload.link_label),
    section_id: payload.section_id,
  });
};

export const trackDcaCalculatorViewed = () => {
  track("dca_calculator_viewed", {
    section_id: "dca_calculator",
  });
};

export const trackDcaCalculatorCalculated = (payload: {
  coin: "BTC" | "ETH" | "SOL";
  currency: "USD" | "CHF" | "EUR";
  frequency: "daily" | "weekly" | "monthly";
  buy_count: number;
}) => {
  track("dca_calculator_calculated", {
    ...payload,
    selected_currency: payload.currency,
  });
};

export const trackDcaCalculatorCtaClicked = (payload: {
  cta: "try_sandbox" | "request_early_access";
}) => {
  track("dca_calculator_cta_clicked", payload);
};

export const trackDcaCalculatorError = (payload: {
  code: "RATE_LIMIT" | "UPSTREAM" | "VALIDATION";
}) => {
  track("dca_calculator_error", payload);
};
